import { useCallback, useState } from 'react';
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import YAML from 'yaml';
import clsx from 'clsx';

type PathSeg = string | number;

function pathKey(segments: PathSeg[]): string {
  return JSON.stringify(segments);
}

function isContainer(value: unknown): value is Record<string, unknown> | unknown[] {
  return value !== null && typeof value === 'object';
}

function collapsedPreview(value: unknown): string {
  if (Array.isArray(value)) {
    return `[${value.length} item${value.length === 1 ? '' : 's'}]`;
  }
  if (value && typeof value === 'object') {
    const n = Object.keys(value as object).length;
    return `{${n} key${n === 1 ? '' : 's'}}`;
  }
  return '';
}

function formatLabel(segments: PathSeg[]): string {
  if (segments.length === 0) return '';
  const last = segments[segments.length - 1];
  return typeof last === 'number' ? `[${last}]` : String(last);
}

/** Prism-like YAML token colors (coy-ish: keys violet, strings green, numbers blue, bool/null distinct). */
function scalarValueClass(value: unknown): string {
  if (value === null || value === undefined) {
    return 'text-rose-600 dark:text-rose-400';
  }
  switch (typeof value) {
    case 'string':
      return 'text-emerald-700 dark:text-emerald-400';
    case 'number':
    case 'bigint':
      return 'text-sky-700 dark:text-sky-400';
    case 'boolean':
      return 'text-amber-700 dark:text-amber-400';
    default:
      return 'text-zinc-800 dark:text-zinc-200';
  }
}

function YamlScalar({ segments, value, depth }: { segments: PathSeg[]; value: unknown; depth: number }) {
  const label = formatLabel(segments);
  const yaml = YAML.stringify(value).replace(/\n$/, '');
  const multiline = yaml.includes('\n');
  const valueClass = scalarValueClass(value);

  return (
    <div className={clsx('font-mono text-xs', multiline && 'py-0.5')} style={{ paddingLeft: depth * 12 }}>
      {label && <span className="text-violet-600 dark:text-violet-400">{label}: </span>}
      {multiline ? (
        <pre
          className={clsx(
            'mt-1 whitespace-pre-wrap wrap-break-word border-l border-zinc-200 pl-2 dark:border-zinc-600',
            valueClass,
          )}
        >
          {yaml}
        </pre>
      ) : (
        <span className={clsx('break-all', valueClass)}>{yaml}</span>
      )}
    </div>
  );
}

function ToggleRow({
  label,
  collapsed,
  onToggle,
  preview,
  depth,
}: {
  label: string;
  collapsed: boolean;
  onToggle: () => void;
  preview: string;
  depth: number;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className={clsx(
        'flex w-full items-start gap-1 rounded px-1 py-0.5 text-left font-mono text-xs',
        'text-zinc-800 hover:bg-zinc-100 dark:text-zinc-200 dark:hover:bg-zinc-800',
      )}
      style={{ paddingLeft: depth * 12 }}
    >
      <span className="mt-0.5 shrink-0 text-zinc-500 dark:text-zinc-400">
        {collapsed ? <ChevronRightIcon className="size-3.5" /> : <ChevronDownIcon className="size-3.5" />}
      </span>
      <span>
        <span className="text-violet-600 dark:text-violet-400">{label}:</span>
        {collapsed && preview && <span className="ml-2 text-zinc-500 dark:text-zinc-400">{preview}</span>}
      </span>
    </button>
  );
}

function YamlBranch({
  segments,
  value,
  depth,
  collapsedPaths,
  toggle,
}: {
  segments: PathSeg[];
  value: unknown;
  depth: number;
  collapsedPaths: Set<string>;
  toggle: (pk: string) => void;
}) {
  const pk = pathKey(segments);
  const isRoot = segments.length === 0;

  if (!isContainer(value)) {
    return <YamlScalar segments={segments} value={value} depth={depth} />;
  }

  const collapsed = !isRoot && collapsedPaths.has(pk);
  const label = formatLabel(segments);
  const preview = collapsedPreview(value);

  const children = Array.isArray(value) ? (
    value.map((item, i) => (
      <YamlBranch
        key={`${pk}-${i}`}
        segments={[...segments, i]}
        value={item}
        depth={depth + 1}
        collapsedPaths={collapsedPaths}
        toggle={toggle}
      />
    ))
  ) : (
    Object.entries(value as Record<string, unknown>).map(([k, v]) => (
      <YamlBranch
        key={`${pk}-${k}`}
        segments={[...segments, k]}
        value={v}
        depth={depth + 1}
        collapsedPaths={collapsedPaths}
        toggle={toggle}
      />
    ))
  );

  return (
    <div className="min-w-0">
      {!isRoot && (
        <ToggleRow
          label={label}
          collapsed={collapsed}
          onToggle={() => toggle(pk)}
          preview={preview}
          depth={depth}
        />
      )}
      {(!collapsed || isRoot) && (
        <div
          className={clsx(
            !isRoot && 'ml-2 border-l border-zinc-200 pl-2 dark:border-zinc-700',
            isRoot && 'space-y-0.5',
          )}
        >
          {children}
        </div>
      )}
    </div>
  );
}

export function CollapsibleYamlView({ data }: { data: unknown }) {
  const [collapsedPaths, setCollapsedPaths] = useState<Set<string>>(() => new Set());

  const toggle = useCallback((pk: string) => {
    setCollapsedPaths((prev) => {
      const next = new Set(prev);
      if (next.has(pk)) next.delete(pk);
      else next.add(pk);
      return next;
    });
  }, []);

  if (data === undefined) {
    return (
      <div className="max-h-[80vh] overflow-auto rounded-md border border-zinc-200 bg-zinc-50 p-4 text-sm text-zinc-500 dark:border-zinc-700 dark:bg-zinc-900/80 dark:text-zinc-400">
        No matching keys.
      </div>
    );
  }

  return (
    <div className="max-h-[80vh] overflow-auto rounded-md border border-zinc-200 bg-zinc-50 p-3 dark:border-zinc-700 dark:bg-zinc-900/80">
      <YamlBranch segments={[]} value={data} depth={0} collapsedPaths={collapsedPaths} toggle={toggle} />
    </div>
  );
}
